import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { ProposalPresentation, type Snapshot } from '@/components/proposal/presentation'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/lib/api'
import { meQuery } from '@/lib/session'
import { useTitle } from '@/lib/use-title'

/** Full-screen staff preview: exactly what the client will see, without the accept form. */
export const Route = createFileRoute('/preview/$id')({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery)
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } })
    if (me.mustChangePassword) throw redirect({ to: '/change-password' })
  },
  component: PreviewPage,
})

function PreviewPage() {
  const { id } = Route.useParams()
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['quote-preview', id],
    queryFn: () =>
      api.get<{ snapshot: Snapshot; contentHash: string; frozen: boolean }>(
        `/quotes/${id}/preview`,
      ),
  })
  useTitle(data ? `Preview: ${data.snapshot.title}` : 'Preview')

  return (
    <>
      <div className="sticky top-0 z-50 flex items-center gap-3 border-b bg-card/95 px-4 py-2 backdrop-blur print:hidden">
        <Link
          to="/quotes/$id"
          params={{ id }}
          className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm font-medium hover:bg-secondary"
        >
          <ChevronLeft className="size-4" aria-hidden />
          Back to quote
        </Link>
        {data && (
          <span className="text-sm text-muted-foreground">
            {data.frozen
              ? 'The version the client has'
              : 'Draft preview: changes you save appear here'}
          </span>
        )}
      </div>
      {isError ? (
        <p role="alert" className="p-6 text-destructive">
          {error.message}
        </p>
      ) : isPending ? (
        <Skeleton className="m-4 h-[80dvh] rounded-xl" />
      ) : (
        <ProposalPresentation snapshot={data.snapshot} mode="preview" />
      )}
    </>
  )
}
